import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export const PROFILE_SETTINGS_SECTIONS = Object.freeze([
  {
    key: 'general',
    path: 'preferences',
    parentRouteName: 'personal_preferences_settings',
    routeName: 'personal_preferences_settings_index',
    icon: 'settings',
    labelKey: 'SETTINGS_CENTER.GENERAL',
    searchKeys: [
      'GENERAL_PREFERENCES.CONVERSATIONS.TITLE',
      'GENERAL_PREFERENCES.CONVERSATIONS.LANDING.TITLE',
      'GENERAL_PREFERENCES.CONVERSATIONS.LAYOUT.TITLE',
      'GENERAL_PREFERENCES.CONVERSATIONS.SIDEBAR.TITLE',
      'GENERAL_PREFERENCES.CONVERSATIONS.CONTACT_PANEL.TITLE',
      'GENERAL_PREFERENCES.LIST.PRIORITY.TITLE',
    ],
  },
  {
    key: 'profile',
    path: 'profile',
    parentRouteName: 'profile_settings',
    routeName: 'profile_settings_index',
    icon: 'person',
    labelKey: 'SETTINGS_CENTER.PROFILE',
    legacyHash: '#profile',
  },
  {
    key: 'appearance',
    path: 'appearance',
    parentRouteName: 'appearance_settings',
    routeName: 'appearance_settings_index',
    icon: 'appearance',
    labelKey: 'SETTINGS_CENTER.APPEARANCE',
    legacyHash: '#appearance',
  },
  {
    key: 'email-signature',
    path: 'email-signature',
    parentRouteName: 'email_signature_settings',
    routeName: 'email_signature_settings_index',
    icon: 'mail',
    labelKey: 'SETTINGS_CENTER.EMAIL_SIGNATURE',
    legacyHash: '#email-signature',
  },
  {
    key: 'security',
    path: 'security',
    parentRouteName: 'security_settings',
    routeName: 'security_settings_index',
    icon: 'lock-closed',
    labelKey: 'SETTINGS_CENTER.SECURITY',
    legacyHash: '#security',
  },
]);

export const profileSettingsSectionForHash = (hash = '') =>
  PROFILE_SETTINGS_SECTIONS.find((section) => section.legacyHash === hash) ||
  PROFILE_SETTINGS_SECTIONS.find((section) => section.key === 'profile');

export const buildSettingsCenterGroups = () => [
  {
    key: 'personal',
    labelKey: 'SETTINGS_CENTER.PERSONAL',
    items: PROFILE_SETTINGS_SECTIONS.map(
      ({ key, icon, labelKey, routeName, searchKeys }) => ({
        key,
        icon,
        labelKey,
        routeName,
        searchKeys,
        roles: ['administrator', 'agent'],
      })
    ),
  },
  {
    key: 'workspace',
    labelKey: 'SETTINGS_CENTER.WORKSPACE',
    items: [
      {
        key: 'account',
        icon: 'briefcase',
        labelKey: 'SETTINGS_CENTER.ACCOUNT',
        routeName: 'general_settings_index',
        roles: ['administrator'],
      },
      {
        // I numeri dell'account per l'albergatore: pagina permanente, si
        // aggiorna a ogni apertura e vale come argomento di rinnovo.
        key: 'activity',
        icon: 'arrow-trending-lines',
        labelKey: 'SETTINGS_CENTER.ACTIVITY',
        routeName: 'settings_analytics',
        searchKeys: [
          'SETTINGS_CENTER.ACTIVITY_DESCRIPTION',
          'ACCOUNT_ANALYTICS.METRICS.CONVERSATIONS',
          'ACCOUNT_ANALYTICS.METRICS.REPLIES',
          'ACCOUNT_ANALYTICS.CALENDAR.TITLE',
          'ACCOUNT_ANALYTICS.TABS.CHANNELS',
        ],
        roles: ['administrator'],
      },
      {
        key: 'team-access',
        icon: 'people-team',
        labelKey: 'SETTINGS_CENTER.TEAM_ACCESS',
        routeName: 'agent_list',
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.AGENT_MANAGEMENT,
      },
      {
        key: 'inbox-channels',
        icon: 'mail-inbox-all',
        labelKey: 'SETTINGS_CENTER.INBOX_CHANNELS',
        routeName: 'settings_inbox_list',
        activeRouteNames: [
          'settings_inbox',
          'settings_inbox_list',
          'settings_inbox_new',
          'settings_inbox_finish',
          'settings_inboxes_add_agents',
          'settings_inboxes_page_channel',
          'settings_inbox_show',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
      },
    ],
  },
  {
    key: 'assistant-channels',
    labelKey: 'SETTINGS_CENTER.ASSISTANT_CHANNELS',
    items: [
      {
        key: 'chatbot',
        icon: 'bot',
        labelKey: 'SETTINGS_CENTER.CHATBOT',
        routeName: 'agent_ai_chatbot_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.CHATBOT_DESCRIPTION',
          'AGENT_AI.DATA.WIDGET_CONFIG.TITLE',
          'AGENT_AI.DATA.WIDGET_CONFIG.FONT',
          'AGENT_AI.DATA.WIDGET_CONFIG.PRIMARY_COLOR',
          'AGENT_AI.CHATBOT_INSTALL.TITLE',
          'AGENT_AI.TEST.CHATBOT.TITLE',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
      {
        // Come ti organizza la posta in arrivo: non manda niente a nessuno,
        // decide solo cosa ti mette davanti per primo.
        key: 'priority',
        icon: 'arrow-sort',
        labelKey: 'SETTINGS_CENTER.PRIORITY',
        routeName: 'agent_ai_priority_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.PRIORITY_DESCRIPTION',
          'AGENT_AI.CHANNELS.CAPABILITIES.PRIORITIZES',
          'AGENT_AI.FLOW_RULES.PRIORITY_TITLE',
          'AGENT_AI.FLOW_RULES.PRIORITY_STANDARD',
          'AGENT_AI.AUTOMATIONS_PAGE.PRIORITY_SECTION',
          'CONVERSATION.PRIORITY.TITLE',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
      {
        // Tutto cio' che l'assistente fa senza che nessuno prema invio:
        // ordina per priorita', prepara bozze, invia risposte, manda i
        // messaggi al momento giusto. La rotta resta quella storica perche'
        // la Panoramica dell'Agente AI ci punta.
        key: 'automations',
        icon: 'calendar-clock',
        labelKey: 'SETTINGS_CENTER.AUTOMATIONS',
        routeName: 'agent_ai_automations_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.AUTOMATIONS_DESCRIPTION',
          'AGENT_AI.CHANNELS.CAPABILITIES.AUTO_DRAFTS',
          'AGENT_AI.CHANNELS.CAPABILITIES.AUTO_SENDS',
          'AGENT_AI.FLOW_RULES.DRAFTS_TITLE',
          'AUTOMATION_PANEL.TITLE',
          'AUTOMATION_PANEL.SCHEDULES',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
      {
        key: 'quick-commands',
        icon: 'quick-commands',
        labelKey: 'SETTINGS_CENTER.QUICK_COMMANDS',
        routeName: 'agent_ai_quick_commands_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.QUICK_COMMANDS_DESCRIPTION',
          'SIDEBAR.QUICK_COMMANDS',
          'INTEGRATION_SETTINGS.QUICK_COMMANDS.ADD_NEW',
          'INTEGRATION_SETTINGS.QUICK_COMMANDS.SAVE_CHANGES',
          'INTEGRATION_SETTINGS.QUICK_COMMANDS.PROMPT_TEXT',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
      {
        key: 'voice',
        icon: 'call',
        labelKey: 'SETTINGS_CENTER.VOICE',
        routeName: 'agent_ai_voice_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.VOICE_DESCRIPTION',
          'AGENT_AI.DATA.VOICE_NAME.TITLE',
          'AGENT_AI.DATA.VOICE_GREETING.TITLE',
          'AGENT_AI.DATA.VOICE_TRANSFER.TITLE',
          'AGENT_AI.VOICE_FORWARD.TITLE',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
      {
        // Sempre in elenco come chatbot e telefono: chi non ha il concierge
        // trova la pagina che spiega cos'e' e che lo attiviamo noi.
        key: 'concierge',
        icon: 'alert',
        labelKey: 'SETTINGS_CENTER.CONCIERGE',
        routeName: 'agent_ai_concierge_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.CONCIERGE_DESCRIPTION',
          'AGENT_AI.CONCIERGE.QR.TITLE',
          'AGENT_AI.CONCIERGE.QR.DOWNLOAD_PNG',
          'AGENT_AI.CONCIERGE.QR.PRINT',
          'AGENT_AI.CONCIERGE.HOME_TEXTS.TITLE',
          'AGENT_AI.CONCIERGE.TILES.TITLE',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
    ],
  },
  {
    key: 'integrations',
    labelKey: 'SETTINGS_CENTER.INTEGRATIONS',
    items: [
      {
        key: 'pms',
        icon: 'link',
        labelKey: 'SETTINGS_CENTER.PMS',
        routeName: 'agent_ai_pms_settings_index',
        searchKeys: [
          'SETTINGS_CENTER.PMS_DESCRIPTION',
          'AGENT_AI.PREVENTIVATORE.TITLE',
          'AGENT_AI.PREVENTIVATORE.SUBTITLE',
          'AGENT_AI.PREVENTIVATORE.VERIFY',
          'AGENT_AI.PREVENTIVATORE.CHANNEL_TOGGLE.TITLE',
        ],
        roles: ['administrator'],
        featureFlag: FEATURE_FLAGS.HOTEL_DATA,
        alwaysVisibleOnOtelloInstances: true,
      },
    ],
  },
];

export const isSettingsCenterItemActive = ({ item = {}, route = {} } = {}) => {
  const routeNames = item.activeRouteNames || [item.routeName];
  return routeNames.includes(route.name);
};

// Voci visibili per ruolo e feature flag: stesse regole per la colonna del
// desktop (SettingsCenterNav) e per l'elenco di Otello da mobile.
export const filterSettingsCenterGroups = ({
  groups = [],
  currentRole = '',
  isCustomBrandedInstance = false,
  isFeatureEnabled = () => false,
} = {}) =>
  groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (!item.roles.includes(currentRole)) return false;
        if (item.alwaysVisibleOnOtelloInstances && !isCustomBrandedInstance) {
          return true;
        }
        if (!item.featureFlag) return true;
        return isFeatureEnabled(item.featureFlag);
      }),
    }))
    .filter((group) => group.items.length);

// Ricerca nelle voci: etichetta e parole chiave tradotte. Una voce figlia che
// corrisponde tiene visibile anche la madre.
export const searchSettingsCenterGroups = ({
  groups = [],
  query = '',
  translate = (key) => key,
} = {}) => {
  const normalizedQuery = String(query || '').toLocaleLowerCase();
  if (!normalizedQuery) return groups;

  return groups
    .map((group) => {
      const matchingKeys = new Set(
        group.items
          .filter((item) => {
            const searchText = [item.labelKey, ...(item.searchKeys || [])]
              .map((key) => translate(key))
              .join(' ')
              .toLocaleLowerCase();
            return searchText.includes(normalizedQuery);
          })
          .map((item) => item.key)
      );

      group.items.forEach((item) => {
        if (item.parentKey && matchingKeys.has(item.key)) {
          matchingKeys.add(item.parentKey);
        }
      });

      return {
        ...group,
        items: group.items.filter((item) => matchingKeys.has(item.key)),
      };
    })
    .filter((group) => group.items.length);
};

// ISO R-23: gli allegati contrattuali (Termini, SLA, Privacy Policy, DPA,
// AI Act) non sono una voce del menu, dove si confondevano con i documenti
// dell'hotel: stanno in fondo alla colonna, come link "Legale e privacy" che
// apre un pannello. Solo lettura, solo admin: il server controlla di nuovo il
// ruolo prima di dare i PDF. La pagina storica (routeName) resta per i link
// salvati e per Otello da mobile.
export const SETTINGS_CENTER_FOOTER_LINKS = Object.freeze([
  Object.freeze({
    key: 'legal-privacy',
    icon: 'document',
    labelKey: 'SETTINGS_CENTER.LEGAL_PRIVACY',
    routeName: 'settings_documents_index',
    searchKeys: [
      'SETTINGS_CENTER.LEGAL_PRIVACY_SUBTITLE',
      'LEGAL_DOCUMENTS.SEARCH_KEYWORDS',
    ],
    roles: ['administrator'],
  }),
]);

export const filterSettingsCenterFooterLinks = ({
  links = SETTINGS_CENTER_FOOTER_LINKS,
  currentRole = '',
} = {}) => links.filter((link) => link.roles.includes(currentRole));

// La ricerca trova anche i link in fondo alla colonna, con le stesse regole
// delle voci; a ricerca vuota non ne mostra nessuno (il link resta in fondo).
export const searchSettingsCenterFooterLinks = ({
  links = [],
  query = '',
  translate = (key) => key,
} = {}) => {
  if (!String(query || '').trim()) return [];
  return searchSettingsCenterGroups({
    groups: [{ key: 'footer', items: links }],
    query,
    translate,
  }).flatMap((group) => group.items);
};

// Otello da mobile (docs/mobile-web/README.md, "Impostazioni"): voci che si
// aprono sul telefono. Le altre hanno troppe opzioni e restano "Dal computer".
export const MOBILE_SETTINGS_ITEM_KEYS = Object.freeze([
  'general',
  'profile',
  'appearance',
  'email-signature',
  'security',
  'activity',
  'team-access',
  'priority',
  'automations',
  'quick-commands',
  'voice',
  'concierge',
]);

export const isSettingsItemOnMobile = (item = {}) =>
  MOBILE_SETTINGS_ITEM_KEYS.includes(item.key);

// Chiave della descrizione mostrata sotto la voce nell'elenco mobile.
export const mobileSettingsItemDescriptionKey = (item = {}) =>
  `SETTINGS_CENTER.MOBILE_DESCRIPTIONS.${String(item.key || '')
    .toUpperCase()
    .replace(/-/g, '_')}`;
